// Shared helpers for the business-case overlay components.
//
// These adapt the portal API's `PortalCostItem` / `PortalSavingsItem` shapes to
// the copied financial lib's raw-bucket inputs and provide the annualisation /
// grouping logic the accordions display. The lib already reads `.cost` /
// `.savings` + `.cost_driver.category` directly off the portal items, so the
// bucket adaptation is purely a one_time / recurring split.

import type { PortalCostItem, PortalSavingsItem } from '../../api/types';

export interface RecurringIntervalInfo {
  label: string;
  periodsPerYear: number;
}

/** Recurring interval scale (1=monthly … 5=yearly), mirroring the main app. */
export const RECURRING_INTERVALS: Record<number, RecurringIntervalInfo> = {
  1: { label: 'Monthly', periodsPerYear: 12 },
  2: { label: 'Bi-Monthly', periodsPerYear: 6 },
  3: { label: 'Quarterly', periodsPerYear: 4 },
  4: { label: 'Half-Yearly', periodsPerYear: 2 },
  5: { label: 'Yearly', periodsPerYear: 1 },
};

export function periodsPerYear(interval: number | null | undefined): number {
  if (interval == null) return 1;
  return RECURRING_INTERVALS[interval]?.periodsPerYear ?? 1;
}

export function intervalLabel(interval: number | null | undefined): string {
  if (interval == null) return 'Yearly';
  return RECURRING_INTERVALS[interval]?.label ?? 'Yearly';
}

/** A raw {one_time, recurring} bucket the copied lib consumes. */
export interface RawBucket {
  one_time: Array<Record<string, unknown>>;
  recurring: Array<Record<string, unknown>>;
}

export function toCostBucket(costs: PortalCostItem[]): RawBucket {
  return {
    one_time: costs.filter((c) => !c.recurring) as unknown as Array<
      Record<string, unknown>
    >,
    recurring: costs.filter((c) => c.recurring) as unknown as Array<
      Record<string, unknown>
    >,
  };
}

export function toSavingsBucket(savings: PortalSavingsItem[]): RawBucket {
  return {
    one_time: savings.filter((s) => !s.recurring) as unknown as Array<
      Record<string, unknown>
    >,
    recurring: savings.filter((s) => s.recurring) as unknown as Array<
      Record<string, unknown>
    >,
  };
}

/**
 * Annualised total of a list of cost rows: recurring rows are multiplied by
 * their periods/year; one-time rows are counted once.
 */
export function annualisedCostTotal(costs: PortalCostItem[]): number {
  return costs.reduce((total, c) => {
    const amount = c.cost ?? 0;
    if (c.recurring) return total + amount * periodsPerYear(c.recurring_interval);
    return total + amount;
  }, 0);
}

/** Annualised total of a list of savings rows (same rule as costs). */
export function annualisedSavingsTotal(savings: PortalSavingsItem[]): number {
  return savings.reduce((total, s) => {
    const amount = s.savings ?? 0;
    if (s.recurring) return total + amount * periodsPerYear(s.recurring_interval);
    return total + amount;
  }, 0);
}

/** Group an array by a string key, alpha-sorting the resulting sections. */
export function groupByCategory<T>(
  items: T[],
  keyOf: (item: T) => string,
): Array<{ category: string; items: T[] }> {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const key = keyOf(item) || 'Uncategorised';
    const bucket = map.get(key);
    if (bucket) bucket.push(item);
    else map.set(key, [item]);
  }
  return [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([category, list]) => ({ category, items: list }));
}

export type FilterChip = 'all' | 'one_time' | 'recurring';
